import { Subscription } from '../rpc'
import { mockLog } from '../MockDebug'
import {
  ActivityActionEvent,
  ActivityApi,
  ActivityIntent,
  EndActivityInput,
  StartActivityResult,
  UpdateActivityInput,
  isActivityActionEvent,
} from './ActivityApi'

const TAG = 'Mock Activity'

export interface MockActivityApiOptions {
  /** When false, every start reports `skipped/unsupported_surface`. */
  supported?: boolean
}

export class MockActivityApi implements ActivityApi {
  private readonly active = new Map<string, ActivityIntent>()
  private readonly listeners = new Set<(event: ActivityActionEvent) => void>()
  private readonly supported: boolean

  constructor(options: MockActivityApiOptions = {}) {
    this.supported = options.supported ?? true
  }

  async startActivityAsync(intent: ActivityIntent): Promise<StartActivityResult> {
    const activityId = `mock-activity:${intent.key}`

    if (!this.supported) {
      mockLog(TAG, `startActivityAsync skipped key=${intent.key} (no eligible surface)`)
      return { activityId, status: 'skipped', reason: 'unsupported_surface' }
    }

    this.active.set(activityId, intent)
    mockLog(TAG, `startActivityAsync key=${intent.key} activityId=${activityId}`)
    return { activityId, status: 'accepted' }
  }

  updateActivity(input: UpdateActivityInput): void {
    const intent = this.active.get(input.activityId)
    if (!intent) return
    this.active.set(input.activityId, { ...intent, state: input.state })
    mockLog(TAG, `updateActivity activityId=${input.activityId} status=${input.state.status}`)
  }

  async endActivityAsync(input: EndActivityInput): Promise<void> {
    this.active.delete(input.activityId)
    mockLog(TAG, `endActivityAsync activityId=${input.activityId}`)
  }

  onActivityAction(callback: (event: ActivityActionEvent) => void): Subscription {
    this.listeners.add(callback)
    return { unsubscribe: () => this.listeners.delete(callback) }
  }

  /** Test/playground affordance: pretend a native surface issued an action. */
  simulateActivityAction(event: ActivityActionEvent): void {
    if (!isActivityActionEvent(event)) return
    if (!this.active.has(event.activityId)) return
    for (const listener of [...this.listeners]) listener(event)
  }
}
